import React, { useState } from "react";
import { CognitoUserPool, CognitoUser } from "amazon-cognito-identity-js";
import { Form, Button, Alert, Container, Row, Col, Card } from "react-bootstrap";
import "./ForgotPassword.css";
import { emailVerificationStatus, userNameValidation } from "../../Helper/companySignupApi";
import SuccessModal from "../../components/SuccessModal";
import { useNavigate } from "react-router-dom";
import { CircularProgress } from "@mui/material";

const poolData = {
  UserPoolId: process.env.REACT_APP_USER_POOL_ID,
  ClientId: process.env.REACT_APP_CLIENT_ID
};

const userPool = new CognitoUserPool(poolData);

const ForgotPassword = () => {
  const [username, setUsername] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [stage, setStage] = useState(1); // Stage 1: Initial stage
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const getUser = () => {
    return new CognitoUser({
      Username: username,
      Pool: userPool
    });
  };

  const sendCode = async (event) => {
    setErrorMessage("");
    setLoading(true)
    event.preventDefault();
    const cognitoUser = getUser();

    try {
      const res = await userNameValidation(username);
      if (res.data.body === "not_exist") {
        setErrorMessage("Username doesn't exist.");
        return;
      }

      const isVerified = await verifyUserAttributes(username);
      if (!isVerified) {
        return;
      }

      await new Promise((resolve, reject) => {
        cognitoUser.forgotPassword({
          onSuccess: () => {
            setStage(2); // Move to stage 2 for entering verification code
            setErrorMessage("");
            resolve();
            setLoading(false)
          },
          onFailure: (err) => {
            setErrorMessage(`Failed to send code: ${err.message}`);
            reject(err);
            setLoading(false)
          },
          inputVerificationCode: () => {
            setStage(2); // Consider moving to stage 2 here if needed sooner
            resolve();
            setLoading(false);
          }
        });
      });
    } catch (err) {
      setErrorMessage(`Failed to send code: ${err.message}`);
      setLoading(false);
    }
  };


  const verifyUserAttributes = async (username) => {
    const data = {
      userPoolId: process.env.REACT_APP_USER_POOL_ID,
      userName: username
    }

    const response = await emailVerificationStatus(data);
    console.log(response);

    if (response?.data?.status === 200) {
      return true;
    }
    setErrorMessage(response?.data?.message);
    return false;
  };

  const handleSubmitNewPassword = async (event) => {
    event.preventDefault();
    const cognitoUser = getUser();

    if (newPassword !== confirmPassword) {
      setErrorMessage("Passwords do not match.");
      return;
    }

    try {
      await new Promise((resolve, reject) => {
        cognitoUser.confirmPassword(verificationCode, newPassword, {
          onSuccess: resolve,
          onFailure: reject,
        });
      });
      setShowModal(true);
    } catch (err) {
      setErrorMessage(`Failed to change password: ${err.message}`);
    }
  };

  const handleRedirectToLogin = async () => {
    navigate('/signin');
  }

  const renderStage = () => {
    switch (stage) {
      case 1:
        return (
          <Form onSubmit={sendCode} className="p-4">
            <Form.Group controlId="formUsername" className="mb-3">
              <Form.Label>Username</Form.Label>
              <Form.Control
                type="text"
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                required
              />
            </Form.Group>
            <Button disabled={loading} variant="primary" type="submit" className="w-100 theme-bg">
              Send Verification Code
            </Button>
            {loading && (
              <div className="text-center w-100 mt-3">
                <CircularProgress />
              </div>
            )}
            {errorMessage && <Alert variant="danger" className="mt-3">{errorMessage}</Alert>}
          </Form>
        );
      case 2:
        return (
          <Form onSubmit={handleSubmitNewPassword} className="p-4">
            <Form.Group controlId="formVerificationCode" className="mb-3">
              <Form.Label>Verification Code</Form.Label>
              <Form.Control
                type="text"
                value={verificationCode}
                onChange={(event) => setVerificationCode(event.target.value)}
                required
              />
            </Form.Group>
            <Form.Group controlId="formNewPassword" className="mb-3">
              <Form.Label>New Password</Form.Label>
              <Form.Control
                type="password"
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                required
              />
            </Form.Group>
            <Form.Group controlId="formConfirmPassword" className="mb-3">
              <Form.Label>Confirm New Password</Form.Label>
              <Form.Control
                type="password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                required
              />
            </Form.Group>
            <Button variant="primary" type="submit" className="w-100 theme-bg">
              Submit New Password
            </Button>
            {errorMessage && <Alert variant="danger" className="mt-3">{errorMessage}</Alert>}
            <SuccessModal
              isOpen={showModal}
              message="You have successfully changed your password."
              onConfirm={handleRedirectToLogin}
              btnMessage="Go to Login"
            />
          </Form>
        );
      default:
        return null;
    }
  };

  return (
    <Container className="mt-5">
      <Row className="justify-content-md-center">
        <Col md={6}>
          <Card className="forgot-password-card">
            <Card.Body>
              <h2 className="text-center mb-4 forgot-password-title theme-color">Forgot Password</h2>
              {renderStage()}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default ForgotPassword;
